import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import api from "@/axios";
import { routes } from "@/utils/urls";

// Types
export interface Patient {
  memberId: string;
  medicadeId: string;
  memberLastName: string;
  memberFirstName: string;
  memberDob: string;
}

export interface SearchResponse {
  status: number;
  message: string;
  data: Patient[];
}

export interface SearchError {
  error?: string;
  field?: string;
  status?: number;
  message?: string;
}

export interface SearchState {
  results: Patient[];
  loading: boolean;
  error: SearchError | null;
  lastQuery: string;
  lastMessage?: string;
  selectedPatient?: Patient | null;
}

const initialState: SearchState = {
  results: [],
  loading: false,
  error: null,
  lastQuery: "",
  lastMessage: undefined,
  selectedPatient: null,
};

// Note: Previously used dummy patients; removed to rely on API and explicit empty state handling

// Async thunk for searching patients (using dummy data for now)
export const searchPatients = createAsyncThunk<
  SearchResponse,
  { query: string; searchType: "medicaid-id" | "dob-name" },
  { rejectValue: SearchError }
>(
  "search/searchPatients",
  async ({ query, searchType: _searchType }, { rejectWithValue }) => {
    try {
      let filteredPatients: Patient[] = [];

      try {
        const apiResponse = await api.post(routes.search.patients, {
          query: query.trim(),
        });
        const data = apiResponse.data as SearchResponse;
        filteredPatients = data.data;
        if (Array.isArray(data?.data)) {
          return data;
        }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        console.log("error", error);
      }

    if (filteredPatients.length === 0) {
  const message =
    _searchType === "dob-name"
      ? "No patient(s) found matching the search criteria"
      : "No patient found matching the search criteria";

  return {
    status: 404,
    message,
    data: [],
  };
}

      return {
        status: 200,
        message: `Found ${filteredPatients.length} patient(s)`,
        data: filteredPatients,
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error(error)
      return rejectWithValue({
        error: "Search failed",
        status: 500,
      });
    }
  }
);

const searchSlice = createSlice({
  name: "search",
  initialState,
  reducers: {
    clearSearch: (state) => {
      state.results = [];
      state.error = null;
      state.lastQuery = "";
      state.lastMessage = undefined;
    },
    setSearchType: (
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _action: PayloadAction<"medicaid-id" | "dob-name">
    ) => {
      // Clear results when search type changes
      state.results = [];
      state.error = null;
      state.lastQuery = "";
      state.lastMessage = undefined;
    },
    clearError: (state) => {
      state.error = null;
    },
    setSelectedPatient: (state, action: PayloadAction<Patient | null>) => {
      state.selectedPatient = action.payload;
    },
    clearSelectedPatient: (state) => {
      state.selectedPatient = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchPatients.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.lastMessage = undefined;
      })
      .addCase(searchPatients.fulfilled, (state, action) => {
        state.loading = false;
        state.results = action.payload.data;
        state.error = null;
        state.lastQuery = action.meta.arg.query;
        state.lastMessage = action.payload.message;
      })
      .addCase(searchPatients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || { error: "Search failed" };
        state.results = [];
        state.lastMessage = undefined;
      });
  },
});

export const {
  clearSearch,
  setSearchType,
  clearError,
  setSelectedPatient,
  clearSelectedPatient,
} = searchSlice.actions;
export default searchSlice.reducer;
